export type SyncMappingMode = 'nest' | 'merge'

export type SyncPreviewSample = {
  name: string
  kind: 'folder' | 'file'
}

export type SyncPreviewSampleState =
  | { type: 'placeholder' }
  | { type: 'empty' }
  | { type: 'samples'; items: SyncPreviewSample[] }

export function previewSampleState(
  samples: SyncPreviewSample[] | null | undefined,
): SyncPreviewSampleState {
  if (samples == null) {
    return { type: 'placeholder' }
  }
  if (samples.length === 0) {
    return { type: 'empty' }
  }
  return { type: 'samples', items: sortPreviewSamples(samples) }
}

export function sortPreviewSamples(items: SyncPreviewSample[]): SyncPreviewSample[] {
  const byName = (left: SyncPreviewSample, right: SyncPreviewSample) => (
    left.name.localeCompare(right.name, undefined, { numeric: true, sensitivity: 'base' })
  )
  return [
    ...items.filter((item) => item.kind === 'folder').sort(byName),
    ...items.filter((item) => item.kind === 'file').sort(byName),
  ]
}

export function mergedPreviewSampleState(
  perSource: Array<SyncPreviewSample[] | null | undefined>,
): SyncPreviewSampleState {
  const states = perSource.map(previewSampleState)
  if (states.some((state) => state.type === 'placeholder')) {
    return { type: 'placeholder' }
  }
  const items = states.flatMap((state) => (state.type === 'samples' ? state.items : []))
  if (items.length > 0) {
    return { type: 'samples', items: sortPreviewSamples(items) }
  }
  if (states.length > 0 && states.every((state) => state.type === 'empty')) {
    return { type: 'empty' }
  }
  return { type: 'placeholder' }
}

export type NamedFolderNode = {
  folder_id: number
  name: string
  children?: NamedFolderNode[]
}

export function defaultSyncMappingMode({
  destinationIsRoot,
  sourceCount = 1,
}: {
  destinationIsRoot: boolean
  sourceCount?: number
}): SyncMappingMode {
  if (destinationIsRoot || sourceCount > 1) {
    return 'nest'
  }
  return 'merge'
}

export function isMergeToRootBlocked(mode: SyncMappingMode, destinationIsRoot: boolean): boolean {
  return mode === 'merge' && destinationIsRoot
}

export function namesWouldDoubleNest(
  sourceName: string,
  destinationName: string | null | undefined,
  mode: SyncMappingMode,
  destinationIsRoot: boolean,
): boolean {
  if (mode !== 'nest' || destinationIsRoot || !destinationName) {
    return false
  }
  return sourceName.trim().toLowerCase() === destinationName.trim().toLowerCase()
}

export function syncMappingOverrideCopy({
  destinationIsRoot,
  sourceCount,
  sourceName,
  destinationName,
}: {
  destinationIsRoot: boolean
  sourceCount: number
  sourceName?: string
  destinationName?: string
}): {
  nestLabel: string
  nestHint: string
  mergeLabel: string
  mergeHint: string
} | null {
  if (destinationIsRoot) {
    return null
  }
  if (sourceCount > 1) {
    return {
      nestLabel: 'Add each as a subfolder',
      nestHint: destinationName
        ? `Creates a folder for each selection inside ${destinationName}.`
        : 'Creates a folder for each selection inside the destination.',
      mergeLabel: 'Merge into this folder',
      mergeHint: destinationName
        ? `Files and subfolders from every selection go directly into ${destinationName}.`
        : 'Files and subfolders from every selection go directly into the destination.',
    }
  }
  return {
    nestLabel: sourceName && destinationName
      ? `Create ${sourceName} inside ${destinationName}`
      : 'Add as a subfolder',
    nestHint: 'Its files and subfolders go in that new folder.',
    mergeLabel: destinationName
      ? `Put contents into ${destinationName}`
      : 'Put contents into this folder',
    mergeHint: destinationName
      ? `Files and subfolders go directly into ${destinationName}.`
      : 'Files and subfolders go directly into the destination.',
  }
}

export function pruneTreeToSelectedFolder<T extends NamedFolderNode>(
  tree: T[],
  folderId: number,
): T[] | null {
  for (const node of tree) {
    if (node.folder_id === folderId) {
      return [{ ...node, children: [] }]
    }
    if (node.children?.length) {
      const childPath = pruneTreeToSelectedFolder(node.children as T[], folderId)
      if (childPath) {
        return [{ ...node, children: childPath }]
      }
    }
  }
  return null
}

export function attachChildrenToFolder<T extends NamedFolderNode>(
  tree: T[],
  folderId: number,
  extra: T[],
): T[] {
  return tree.map((node) => {
    if (node.folder_id === folderId) {
      return { ...node, children: extra }
    }
    if (node.children?.length) {
      return { ...node, children: attachChildrenToFolder(node.children as T[], folderId, extra) }
    }
    return node
  })
}

export function crimsonFolderNameTrail(
  folderId: number,
  tree: NamedFolderNode[],
  trail: string[] = [],
): string[] | null {
  for (const node of tree) {
    const next = [...trail, node.name]
    if (node.folder_id === folderId) {
      return next
    }
    if (node.children?.length) {
      const found = crimsonFolderNameTrail(folderId, node.children, next)
      if (found) {
        return found
      }
    }
  }
  return null
}
